import type { CofaAttachment, CofaIdentity, CofaIdentityStatus, CofaKind, DocumentRow } from '../api/types'

export type CofaCheckStatus = CofaIdentityStatus | 'missing'
export type CofaCheckVariant = 'success' | 'warning' | 'danger' | 'neutral'

/** Short, specific label for the customer-CofA check (middleware
 *  cofa_identity). "Mismatch" alone told people nothing (2026-10-08). */
export function cofaCheckLabel(
  status: CofaCheckStatus,
  identity?: CofaIdentity,
  sourceProblem = false,
): { label: string; variant: CofaCheckVariant } {
  if (sourceProblem) return { label: 'Source CofA wrong', variant: 'danger' }
  switch (status) {
    case 'verified':
      return { label: 'Verified', variant: 'success' }
    case 'verified_human':
      return { label: 'Verified by person', variant: 'success' }
    case 'mismatch':
      return identity?.problem === 'wrong_lot'
        ? { label: 'Wrong lot', variant: 'danger' }
        : { label: 'Wrong product', variant: 'danger' }
    case 'unverifiable':
      if (identity?.problem === 'lot_unreadable') return { label: 'Check lot #', variant: 'warning' }
      if (identity?.problem === 'scanned') return { label: 'Scanned — review', variant: 'warning' }
      return { label: 'Needs review', variant: 'warning' }
    case 'missing':
      return { label: 'No CofA', variant: 'danger' }
    default:
      return { label: 'Checking…', variant: 'neutral' }
  }
}

/** The CofA customers receive with this lot: the SPI CofA for SPI
 *  products (the vendor CofA only feeds its generation), the vendor CofA
 *  for everything else. This is the one the shipping gate checks. */
export function customerCofa(row: DocumentRow): {
  kind: CofaKind
  tab: 'vendor' | 'spi'
  label: string
  attachment: CofaAttachment | null
} {
  return row.is_spi_product
    ? { kind: 'spi_cofa', tab: 'spi', label: 'SPI CofA', attachment: row.spi_cofa }
    : { kind: 'vendor_cofa', tab: 'vendor', label: 'Vendor CofA', attachment: row.vendor_cofa }
}

/** The certificate the check panel acts on: normally the customer CofA;
 *  for an SPI CofA built from the wrong vendor CofA, that vendor CofA. */
export function checkTarget(row: DocumentRow): ReturnType<typeof customerCofa> {
  if (row.source_problem && row.vendor_cofa) {
    return { kind: 'vendor_cofa', tab: 'vendor', label: 'Vendor CofA', attachment: row.vendor_cofa }
  }
  return customerCofa(row)
}

export type Tone = 'ok' | 'warn' | 'bad' | 'neutral'

export interface Explanation {
  tone: Tone
  headline: string
  detail: string
  todo?: string
}

/** Plain-English "what was checked, what was found, what to do". */
export function explainCustomerCofa(row: DocumentRow): Explanation {
  const { label, attachment } = customerCofa(row)
  const lot = row.lot_name
  const product = row.product_name
  if (row.source_problem) {
    const v = row.vendor_cofa?.identity
    const reason = v?.reason ?? 'it is for a different lot'
    return {
      tone: 'bad',
      headline: 'SPI CofA was generated from another lot’s vendor CofA',
      detail: `The SPI CofA itself names lot ${lot}, but the vendor CofA it was generated from doesn’t match: ${reason}. Its test results may belong to another lot. Deliveries of this lot are blocked.`,
      todo: `Look at the vendor CofA. If it’s another lot’s certificate, replace it with lot ${lot}’s vendor CofA and regenerate the SPI CofA. If it does show lot ${lot} and the reading was wrong, mark the vendor CofA verified.`,
    }
  }
  const id = attachment?.identity
  if (!attachment) {
    return {
      tone: 'bad',
      headline: `No ${label} on record`,
      detail: `Customers receive the ${label} with this product, and lot ${lot} doesn't have one. Deliveries of this lot can't be validated.`,
      todo: `Upload the ${label} for lot ${lot}.`,
    }
  }
  if (!id || id.status === 'pending') {
    return { tone: 'neutral', headline: 'Checking this certificate…', detail: 'The automatic check hasn’t run yet. Reload in a minute.' }
  }
  if (id.status === 'verified') {
    const how = id.method === 'vision' ? ' (read from the certificate image)' : ''
    return {
      tone: 'ok',
      headline: 'Confirmed for this lot',
      detail: row.is_spi_product && id.certificate_product
        ? `The certificate shows lot ${lot} and is titled ${id.certificate_product}${how}. It can ship.`
        : `The certificate shows lot ${lot}${how}. It can ship.`,
    }
  }
  if (id.status === 'verified_human') {
    const when = id.verified_at ? ` on ${id.verified_at.slice(0, 10)}` : ''
    return {
      tone: 'ok',
      headline: `Verified by ${id.verified_by_name ?? id.verified_by ?? 'a reviewer'}${when}`,
      detail: `A person confirmed this certificate is for lot ${lot}, ${product}${id.note ? ` (“${id.note}”)` : ''}. It can ship.`,
    }
  }
  if (id.status === 'mismatch' && id.problem === 'wrong_lot') {
    const shown = (id.read_lots ?? []).join(', ') || 'a different lot'
    return {
      tone: 'bad',
      headline: 'Certificate is for a different lot',
      detail: `Reading the certificate image, it shows lot ${shown}, but this is lot ${lot}. Customers would receive another lot’s CofA. Deliveries of this lot are blocked.`,
      todo: `Check the certificate below. If it’s the wrong lot’s certificate, replace it. If it does show ${lot} and the reading was wrong, mark it verified.`,
    }
  }
  if (id.status === 'mismatch') {
    return {
      tone: 'bad',
      headline: 'Wrong certificate',
      detail: `This certificate is for ${id.certificate_product ?? 'a different product'}, but lot ${lot} is ${product}. Customers would receive another product’s CofA. Deliveries of this lot are blocked.`,
      todo: 'Replace it with the correct certificate (or regenerate the SPI CofA from the right vendor CofA). Only if it really is the same material under another name, mark it verified.',
    }
  }
  // unverifiable
  if (id.problem === 'lot_unreadable') {
    return {
      tone: 'warn',
      headline: 'Lot number couldn’t be read automatically',
      detail: id.method === 'vision'
        ? `The check looked for “${lot}” in the certificate’s text and on the certificate image and couldn’t find it${(id.read_lots ?? []).length ? '' : ' (no lot number was legible)'}. Deliveries of this lot are blocked until someone confirms it.`
        : `The check looks for “${lot}” in the certificate’s text and didn’t find it. The number may be printed as an image or graphics. Deliveries of this lot are blocked until someone confirms it.`,
      todo: `Look at the certificate below. If it shows lot ${lot} for ${product}, mark it verified. If the lot is wrong or missing, replace the certificate.`,
    }
  }
  if (id.problem === 'scanned') {
    return {
      tone: 'warn',
      headline: 'Scanned certificate — needs a person to check',
      detail: `This certificate is a scanned image, so it can’t be read automatically. Deliveries of this lot are blocked until someone confirms it.`,
      todo: `Look at the certificate below. If it shows lot ${lot} for ${product}, mark it verified; otherwise replace it.`,
    }
  }
  return {
    tone: 'warn',
    headline: 'Certificate file can’t be read',
    detail: 'The file isn’t a readable PDF. Deliveries of this lot are blocked.',
    todo: 'Replace it with a PDF of the certificate.',
  }
}

